/**
 * Game Progress Sync Service
 * Handles bidirectional sync between localStorage and S3 (via backend API)
 * 
 * Features:
 * - Offline-first: localStorage is primary, S3 is backup
 * - Debounced writes: Batch API calls to reduce load
 * - Optimistic updates: UI updates immediately, sync in background
 * - Conflict resolution: Merge progress intelligently
 * - Error resilience: Graceful degradation, retry logic
 */

import { loadProgress, saveProgress, getErrorMessage } from './api';
import {
  mergeProgress,
  convertToS3Format,
  convertFromS3Format,
  hasSignificantDifference,
} from '../utils/progressMerger';

// Sync configuration
const SYNC_CONFIG = {
  DEBOUNCE_MS: 3000, // Wait 3 seconds after last change before syncing
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 2000,
};

// Sync state (in-memory)
let syncState = {
  status: 'idle', // 'idle' | 'syncing' | 'synced' | 'error' | 'offline'
  lastSyncTime: null,
  pendingSync: null, // Debounce timer
  isOnline: navigator.onLine,
  errorMessage: null,
};

// Event listeners for sync status changes
const syncListeners = new Set();

/**
 * Subscribe to sync status changes
 * @param {Function} callback - Called when sync status changes
 * @returns {Function} Unsubscribe function
 */
export const onSyncStatusChange = (callback) => {
  syncListeners.add(callback);
  // Immediately call with current state
  callback(getSyncStatus());
  
  return () => syncListeners.delete(callback);
};

/**
 * Get current sync status
 * @returns {Object} Sync status object
 */
export const getSyncStatus = () => ({ ...syncState });

/**
 * Update sync status and notify listeners
 * @param {Object} updates - Partial state updates
 */
const updateSyncState = (updates) => {
  syncState = { ...syncState, ...updates };
  
  // Notify all listeners
  const status = getSyncStatus();
  syncListeners.forEach((listener) => {
    try {
      listener(status);
    } catch (error) {
      console.error('❌ Sync listener error:', error);
    }
  });
};

/**
 * Load progress from S3 and merge with localStorage
 * @param {string} userId - Google user ID (sub claim)
 * @param {string} userEmail - User's email (for beta testing)
 * @param {Object} localProgress - Current localStorage progress
 * @returns {Promise<Object>} Merged progress object
 */
export const loadAndMergeProgress = async (userId, userEmail, localProgress) => {
  if (!userId) {
    console.warn('⚠️ Cannot sync: no user ID');
    return localProgress;
  }

  updateSyncState({ status: 'syncing', errorMessage: null });

  try {
    console.log('☁️ Loading progress from S3...');
    
    // Load from S3
    const s3Response = await loadProgress(userId, userEmail);
    
    // Check if this is a first-time user
    if (s3Response.first_time) {
      console.log('ℹ️ First-time user - no S3 data to merge');
      updateSyncState({
        status: 'synced',
        lastSyncTime: new Date().toISOString(),
      });
      return localProgress;
    }

    // Convert S3 format to GameContext format
    const s3Progress = convertFromS3Format(s3Response);

    // Merge with localStorage
    const mergedProgress = mergeProgress(localProgress, s3Progress);

    // Check if merge brought new data
    const hasNewData = hasSignificantDifference(localProgress, mergedProgress);

    if (hasNewData) {
      console.log('🔄 Merged progress has new data from S3');
    }

    updateSyncState({
      status: 'synced',
      lastSyncTime: new Date().toISOString(),
    });

    return { ...mergedProgress, _hasNewData: hasNewData };

  } catch (error) {
    console.error('❌ Failed to load from S3:', error);
    
    const errorMsg = getErrorMessage(error);
    updateSyncState({
      status: error.type === 'network' ? 'offline' : 'error',
      errorMessage: errorMsg,
    });

    // Return local progress on error (offline-first)
    return localProgress;
  }
};

/**
 * Save progress to S3 (debounced)
 * @param {string} userId - Google user ID
 * @param {string} userEmail - User's email (for beta testing)
 * @param {Object} progress - GameContext progress object
 * @param {boolean} immediate - Skip debounce (e.g., on logout)
 */
export const saveProgressToS3 = (userId, userEmail, progress, immediate = false) => {
  if (!userId) {
    console.warn('⚠️ Cannot sync: no user ID (anonymous user)');
    return;
  }

  console.log(`📤 saveProgressToS3 called: userId=${userId}, email=${userEmail}, immediate=${immediate}`);

  // Clear existing debounce timer
  if (syncState.pendingSync) {
    clearTimeout(syncState.pendingSync);
    console.log('🔄 Cleared previous debounce timer');
  }

  // Debounce: wait for user to stop making changes
  const saveFunction = () => performSaveToS3(userId, userEmail, progress);

  if (immediate) {
    console.log('⚡ Immediate sync - no debounce');
    saveFunction();
  } else {
    console.log(`⏱️ Debouncing sync for ${SYNC_CONFIG.DEBOUNCE_MS}ms...`);
    updateSyncState({ status: 'syncing' });
    syncState.pendingSync = setTimeout(saveFunction, SYNC_CONFIG.DEBOUNCE_MS);
  }
};

/**
 * Actually perform the S3 save (called after debounce)
 * @param {string} userId - Google user ID
 * @param {string} userEmail - User's email
 * @param {Object} progress - Progress to save
 * @param {number} attempt - Retry attempt number
 */
const performSaveToS3 = async (userId, userEmail, progress, attempt = 1) => {
  try {
    console.log(`☁️ Saving progress to S3 (attempt ${attempt})...`);

    // Convert to S3 format
    const s3Data = convertToS3Format(progress, userId);

    // Save to S3
    await saveProgress(userId, s3Data, userEmail);

    console.log('✅ Progress saved to S3');
    
    updateSyncState({
      status: 'synced',
      lastSyncTime: new Date().toISOString(),
      errorMessage: null,
      pendingSync: null,
    });

  } catch (error) {
    console.error(`❌ Failed to save to S3 (attempt ${attempt}):`, error);

    const errorMsg = getErrorMessage(error);

    // Retry logic
    if (attempt < SYNC_CONFIG.RETRY_ATTEMPTS && error.type !== 'network') {
      console.log(`🔄 Retrying in ${SYNC_CONFIG.RETRY_DELAY_MS}ms...`);
      
      setTimeout(() => {
        performSaveToS3(userId, userEmail, progress, attempt + 1);
      }, SYNC_CONFIG.RETRY_DELAY_MS);

    } else {
      // Max retries reached or network error
      updateSyncState({
        status: error.type === 'network' ? 'offline' : 'error',
        errorMessage: errorMsg,
        pendingSync: null,
      });
    }
  }
};

/**
 * Force immediate sync (for logout, app close, etc.)
 * @param {string} userId - Google user ID
 * @param {string} userEmail - User's email
 * @param {Object} progress - Current progress
 * @returns {Promise<boolean>} Success status
 */
export const forceSyncNow = async (userId, userEmail, progress) => {
  if (!userId || !progress) return false;

  // Clear any pending debounced sync
  if (syncState.pendingSync) {
    clearTimeout(syncState.pendingSync);
    syncState.pendingSync = null;
  }

  try {
    console.log('⚡ Force sync triggered');
    updateSyncState({ status: 'syncing' });

    const s3Data = convertToS3Format(progress, userId);
    await saveProgress(userId, s3Data, userEmail);

    updateSyncState({
      status: 'synced',
      lastSyncTime: new Date().toISOString(),
      errorMessage: null,
    });

    return true;
  } catch (error) {
    console.error('❌ Force sync failed:', error);
    updateSyncState({
      status: 'error',
      errorMessage: getErrorMessage(error),
    });
    return false;
  }
};

/**
 * Sync calibration data (Sa frequency) separately
 * @param {string} userId - Google user ID
 * @param {string} userEmail - User's email
 * @param {number} saFrequency - Calibrated Sa frequency in Hz
 */
export const syncCalibration = async (userId, userEmail, saFrequency) => {
  if (!userId || !saFrequency) return;

  try {
    console.log(`🎵 Syncing calibration: Sa = ${saFrequency} Hz`);
    
    // We can extend the profile API for this
    // For now, it will be included in progress saves
    // Future enhancement: separate profile endpoint
    
  } catch (error) {
    console.error('❌ Failed to sync calibration:', error);
  }
};

// ============== NETWORK MONITORING ==============

// Monitor online/offline status
window.addEventListener('online', () => {
  console.log('🟢 Back online');
  updateSyncState({ isOnline: true, status: 'idle' });
  
  // Could trigger automatic re-sync here if we have pending changes
});

window.addEventListener('offline', () => {
  console.log('🔴 Offline mode');
  updateSyncState({ isOnline: false, status: 'offline' });
});

// ============== UTILITIES ==============

/**
 * Clear all sync state (for testing/debugging)
 */
export const resetSyncState = () => {
  if (syncState.pendingSync) {
    clearTimeout(syncState.pendingSync);
  }
  
  syncState = {
    status: 'idle',
    lastSyncTime: null,
    pendingSync: null,
    isOnline: navigator.onLine,
    errorMessage: null,
  };
  
  updateSyncState({});
};

/**
 * Get human-readable sync status message
 * @returns {string} Status message for UI
 */
export const getSyncStatusMessage = () => {
  const { status, lastSyncTime, errorMessage } = syncState;

  switch (status) {
    case 'syncing':
      return '⏳ Syncing...';
    
    case 'synced':
      if (lastSyncTime) {
        const elapsed = Date.now() - new Date(lastSyncTime).getTime();
        const minutes = Math.floor(elapsed / 60000);
        if (minutes < 1) return '☁️ Synced just now';
        if (minutes === 1) return '☁️ Synced 1 min ago';
        if (minutes < 60) return `☁️ Synced ${minutes} mins ago`;
        return '☁️ Synced earlier';
      }
      return '☁️ Synced';
    
    case 'offline':
      return '🔌 Offline - changes saved locally';
    
    case 'error':
      return errorMessage || '⚠️ Sync error';
    
    default:
      return '';
  }
};

export default {
  loadAndMergeProgress,
  saveProgressToS3,
  forceSyncNow,
  syncCalibration,
  onSyncStatusChange,
  getSyncStatus,
  getSyncStatusMessage,
  resetSyncState,
};
